import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  InfiniteData,
  infiniteQueryOptions,
  Query,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useDebounceCallback } from 'usehooks-ts';

import { snackbar, SnackbarType } from '@/components/features/Snackbar';
import {
  NOTIFICATIONS_QUERY_KEY,
  NOTIFICATIONS_QUERY_OPTIONS,
} from '@/constant/notifications';
import { useXanoChannel } from '@/hooks/useXanoChannel';
import { notificationsApi } from '@/services/notifications';
import { getNotificationsChannel } from '@/services/xano';
import { useAgents } from '@/store/agents';
import { useUser } from '@/store/auth';
import { Agent } from '@/types/agents';
import {
  BaseNotification,
  Notification,
  NotificationData,
  NotificationsStatusFilter,
  NotificationsUpdateStatus,
} from '@/types/notifications';
import { RealtimeMessage } from '@/types/realtime-xano';

/** Prevents duplicate toasts if the same realtime event is delivered twice. */
const recentToastIds = new Map<string, number>();
const TOAST_DEDUPE_TTL_MS = 8_000;

const shouldShowRealtimeToast = (notificationId: string) => {
  const now = Date.now();
  for (const [id, seenAt] of recentToastIds) {
    if (now - seenAt > TOAST_DEDUPE_TTL_MS) {
      recentToastIds.delete(id);
    }
  }

  if (recentToastIds.has(notificationId)) {
    return false;
  }

  recentToastIds.set(notificationId, now);
  return true;
};

const mapNotification = (notification: BaseNotification): Notification => {
  return {
    ...notification,
    type:
      notification.type === SnackbarType.info && notification.agents_id
        ? SnackbarType.agent
        : notification.type,
  };
};

const notificationsQueryOptions = ({
  status,
  agentIds,
  types,
}: {
  status?: NotificationsStatusFilter;
  agentIds?: Agent['id'][];
  types?: Notification['type'][];
}) =>
  infiniteQueryOptions({
    queryKey: [NOTIFICATIONS_QUERY_KEY, status, agentIds, types],
    queryFn: ({
      pageParam,
    }: {
      status?: NotificationsStatusFilter;
      pageParam: number;
    }) =>
      notificationsApi.getNotifications({ status, pageParam, agentIds, types }),
    getNextPageParam: lastPage => {
      if (!lastPage?.pagination) return undefined;
      const { curPage, pageTotal } = lastPage.pagination;
      // Return next page number if there are more pages, otherwise undefined
      return curPage < pageTotal ? curPage + 1 : undefined;
    },

    select: data => ({
      ...data,
      pages: data.pages.map(page => ({
        ...page,
        items: page?.items.map(mapNotification),
      })),
    }),
    initialPageParam: 1, // API pages typically start at 1
    ...NOTIFICATIONS_QUERY_OPTIONS,
  });

export const useNotificationsChannel = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const { data: agents } = useAgents();
  const agentsRef = useRef(agents);

  useEffect(() => {
    agentsRef.current = agents;
  }, [agents]);

  const invalidateUnreadNotifications = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [NOTIFICATIONS_QUERY_KEY],
      predicate: query => {
        const [, statusFilter] = query.queryKey;
        return statusFilter === NotificationsStatusFilter.UNREAD;
      },
    });
  }, [queryClient]);

  const debounceInvalidateUnreadNotifications = useDebounceCallback(
    invalidateUnreadNotifications,
    500
  );
  const invalidateRef = useRef(debounceInvalidateUnreadNotifications);

  useEffect(() => {
    invalidateRef.current = debounceInvalidateUnreadNotifications;
  }, [debounceInvalidateUnreadNotifications]);

  // Keep a stable listener identity so agent loads / query invalidation do not
  // tear down and re-attach the Xano handler (a known source of duplicate toasts
  // under Vite StrictMode).
  const handleRealtimeNotification = useCallback(
    (data: RealtimeMessage<BaseNotification>) => {
      if (data?.action !== 'event' || !data?.payload?.data) {
        return;
      }

      const newNotification = mapNotification(data.payload.data);

      if (!shouldShowRealtimeToast(newNotification.id)) {
        return;
      }

      const agent = newNotification.agents_id
        ? agentsRef.current?.find(
            agent => +agent.id === +newNotification.agents_id
          )
        : undefined;

      snackbar({
        type: newNotification.type,
        title: newNotification.title,
        description: newNotification.message,
        persistent: newNotification?.persistent,
        targetUrl: newNotification?.targetURL,
        icon: agent?.avatar?.url,
      });

      invalidateRef.current();
    },
    []
  );

  useXanoChannel(
    getNotificationsChannel(user?.company_id),
    handleRealtimeNotification
  );
};
/*
Notification example
{
    "action": "event",
    "options": {
        "channel": "notifications-channel/c50b4628-9d2e-4641-ad15-9cbf596ec5fa"
    },
    "payload": {
        "data": {
            "id": "fe72723e-ad24-493b-95c7-6ebee2b54489",
            "created_at": 1768995241828,
            "user_id": 0,
            "agents_id": 1,
            "company_id": "c50b4628-9d2e-4641-ad15-9cbf596ec5fa",
            "title": "Test",
            "message": "Test",
            "type": "warning",
            "source_type": "BC",
            "updated_at": 1768995241828,
            "broadcast": true,
            "entity_type": "",
            "entity_id": ""
        },
        "dbo_id": 0,
        "row_id": 0
    }
}
*/

export const useGetNotifications = (
  {
    status,
    agentIds,
    types,
  }: {
    status?: NotificationsStatusFilter;
    agentIds?: Agent['id'][];
    types?: Notification['type'][];
  },
  enabled: boolean = true
) => {
  const { user } = useUser();

  const { data, ...notificationsQuery } = useInfiniteQuery({
    ...notificationsQueryOptions({ status, agentIds, types }),
    enabled: Boolean(user?.id) && enabled,
  });

  // Flatten all notifications from all pages
  const notifications = useMemo(() => {
    if (!data?.pages) return undefined;
    return data.pages.flatMap(page => page?.items ?? []);
  }, [data]);

  const totalCount = data?.pages?.[0]?.pagination?.itemsTotal;

  return {
    notifications,
    totalCount,
    ...notificationsQuery,
  };
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    // Optimistic update
    onMutate: async () => {
      const queryFilter = {
        queryKey: [NOTIFICATIONS_QUERY_KEY],
        predicate: (query: Query) => {
          const [, statusFilter] = query.queryKey;

          return statusFilter === NotificationsStatusFilter.UNREAD;
        },
      };

      queryClient.setQueriesData(queryFilter, data => {
        if (!data) return data;
        return {
          pageParams: [],
          pages: [],
        };
      });
    },
    onSettled: () => {
      const queryFilter = {
        queryKey: [NOTIFICATIONS_QUERY_KEY],
        predicate: (query: Query) => {
          const [, statusFilter] = query.queryKey;

          return (
            statusFilter === NotificationsStatusFilter.READ ||
            statusFilter === NotificationsStatusFilter.UNREAD
          );
        },
      };

      queryClient.invalidateQueries(queryFilter);
    },
  });

  return mutation;
};

export const useMarkAllNotificationsAsCleared = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => notificationsApi.markAllAsCleared(),
    // Optimistic update
    onMutate: async () => {
      const queryFilter = {
        queryKey: [NOTIFICATIONS_QUERY_KEY],
        predicate: (query: Query) => {
          const [, statusFilter] = query.queryKey;

          return statusFilter === NotificationsStatusFilter.READ;
        },
      };

      queryClient.setQueriesData(queryFilter, data => {
        if (!data) return data;
        return {
          pageParams: [],
          pages: [],
        };
      });
    },
    onSettled: () => {
      const queryFilter = {
        queryKey: [NOTIFICATIONS_QUERY_KEY],
        predicate: (query: Query) => {
          const [, statusFilter] = query.queryKey;

          return (
            statusFilter === NotificationsStatusFilter.READ ||
            statusFilter === NotificationsStatusFilter.CLEARED
          );
        },
      };

      queryClient.invalidateQueries(queryFilter);
    },
  });

  return mutation;
};

export const useBulkUpdateNotifications = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      notificationIds,
      status,
    }: {
      notificationIds: Notification['id'][];
      status: NotificationsUpdateStatus;
    }) => notificationsApi.bulkUpdate(notificationIds, status),
    // Optimistic update
    onMutate: async ({ notificationIds, status }) => {
      const queryFilter = {
        queryKey: [NOTIFICATIONS_QUERY_KEY],
        predicate: (query: Query) => {
          const [, statusFilter] = query.queryKey;

          if (status === NotificationsUpdateStatus.CLEAR) {
            return (
              statusFilter === NotificationsStatusFilter.UNREAD ||
              statusFilter === NotificationsStatusFilter.READ
            );
          }

          return statusFilter === NotificationsStatusFilter.UNREAD;
        },
      };

      const removeNotificationsFromList = (
        data: InfiniteData<NotificationData | undefined, unknown> | undefined
      ) => {
        if (!data) return data;
        return {
          ...data,
          pages: data.pages.map(page => {
            if (!page) return page;
            return {
              ...page,
              items: page.items?.filter(
                notification => !notificationIds.includes(notification.id)
              ),
            };
          }),
        };
      };

      const previousData = queryClient.getQueriesData(queryFilter);

      queryClient.setQueriesData(queryFilter, removeNotificationsFromList);

      return { previousData };
    },
    onError: (_err, _, context) => {
      // Rollback optimistic update if API call fails
      context?.previousData.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    // Invalidate all lists, to ensure that all lists are up to date
    onSettled: () => {
      // Invalidate all notifications queries
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_QUERY_KEY],
        exact: false,
      });
    },
  });

  return mutation;
};
